import type { FormEvent } from 'react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import BrandLogo from '../components/BrandLogo';
import { api } from '../api/client';

interface PublicJob {
  id: number;
  company_id: number;
  title: string;
  employment_type: string;
  location?: string | null;
  headcount: number;
  description?: string | null;
  target_start_date?: string | null;
}

const initialApplyForm = {
  full_name: '',
  email: '',
  phone: '',
  source: 'Company Site',
  current_company: '',
  current_title: '',
  resume_url: '',
  notes_summary: '',
};

function errorMessageFrom(error: unknown) {
  if (axios.isAxiosError(error)) {
    return error.response?.data?.detail || error.message;
  }
  return error instanceof Error ? error.message : 'Request failed';
}

export default function CareersPage() {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [jobs, setJobs] = useState<PublicJob[]>([]);
  const [selectedJob, setSelectedJob] = useState<PublicJob | null>(null);
  const [applyForm, setApplyForm] = useState(initialApplyForm);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadJobs() {
      try {
        const response = await api.get('/api/public/jobs');
        if (!cancelled) {
          setJobs(response.data);
        }
      } catch (requestError) {
        if (!cancelled) {
          setError(errorMessageFrom(requestError));
        }
      }
    }

    void loadJobs();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadSelectedJob() {
      if (!jobId) {
        setSelectedJob(null);
        return;
      }
      try {
        const response = await api.get(`/api/public/jobs/${jobId}`);
        if (!cancelled) {
          setSelectedJob(response.data);
          setError('');
        }
      } catch (requestError) {
        if (!cancelled) {
          setSelectedJob(null);
          setError(errorMessageFrom(requestError));
        }
      }
    }

    void loadSelectedJob();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  async function handleApply(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedJob) {
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      await api.post(`/api/public/jobs/${selectedJob.id}/apply`, {
        ...applyForm,
        phone: applyForm.phone || null,
        source: applyForm.source || null,
        current_company: applyForm.current_company || null,
        current_title: applyForm.current_title || null,
        resume_url: applyForm.resume_url || null,
        notes_summary: applyForm.notes_summary || null,
      });
      setSuccess('Application submitted. The recruiting team can now review it in the internal pipeline.');
      setApplyForm(initialApplyForm);
    } catch (requestError) {
      setError(errorMessageFrom(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="shell careers-shell">
      <section className="hero">
        <div>
          <BrandLogo />
          <p className="eyebrow">Public careers</p>
          <h1>Open roles and direct applications</h1>
          <p className="lede">
            Browse live requisitions and apply without signing in. Submissions flow directly into the recruiting workspace.
          </p>
        </div>
        <div className="hero-panel">
          <p className="hero-user">Jobs live now: {jobs.length}</p>
          <p className="hero-role">Application route: public careers</p>
          <button type="button" className="ghost-button" onClick={() => navigate('/signin')}>
            Team sign in
          </button>
        </div>
      </section>

      {error ? <p className="error">{error}</p> : null}
      {success ? <p className="helper careers-success">{success}</p> : null}

      <section className="grid dashboard-grid">
        <section className="card careers-list-card">
          <h2>Available jobs</h2>
          {jobs.length ? (
            <ul className="list compact-list">
              {jobs.map((job) => (
                <li key={job.id}>
                  <div>
                    <strong>{job.title}</strong>
                    <span>
                      {job.employment_type}
                      {job.location ? ` • ${job.location}` : ''}
                      {` • ${job.headcount} opening${job.headcount > 1 ? 's' : ''}`}
                    </span>
                  </div>
                  <div className="button-row">
                    <button
                      type="button"
                      className="secondary-button"
                      onClick={() => navigate(`/careers/jobs/${job.id}`)}
                    >
                      View role
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <p>No public openings yet.</p>
          )}
        </section>

        <section className="card careers-detail-card">
          <h2>{selectedJob ? selectedJob.title : 'Select a role'}</h2>
          {selectedJob ? (
            <>
              <p className="helper careers-meta">
                {selectedJob.employment_type}
                {selectedJob.location ? ` • ${selectedJob.location}` : ''}
                {selectedJob.target_start_date ? ` • target start ${selectedJob.target_start_date}` : ''}
              </p>
              <p>{selectedJob.description || 'No job description has been published for this role yet.'}</p>

              <form onSubmit={handleApply}>
                <div className="split">
                  <label>
                    Full name
                    <input
                      value={applyForm.full_name}
                      onChange={(event) => setApplyForm((current) => ({ ...current, full_name: event.target.value }))}
                    />
                  </label>
                  <label>
                    Email
                    <input
                      type="email"
                      value={applyForm.email}
                      onChange={(event) => setApplyForm((current) => ({ ...current, email: event.target.value }))}
                    />
                  </label>
                </div>

                <div className="split split-three">
                  <label>
                    Phone
                    <input
                      value={applyForm.phone}
                      onChange={(event) => setApplyForm((current) => ({ ...current, phone: event.target.value }))}
                    />
                  </label>
                  <label>
                    Source
                    <input
                      value={applyForm.source}
                      onChange={(event) => setApplyForm((current) => ({ ...current, source: event.target.value }))}
                    />
                  </label>
                  <label>
                    Current title
                    <input
                      value={applyForm.current_title}
                      onChange={(event) => setApplyForm((current) => ({ ...current, current_title: event.target.value }))}
                    />
                  </label>
                </div>

                <div className="split">
                  <label>
                    Current company
                    <input
                      value={applyForm.current_company}
                      onChange={(event) => setApplyForm((current) => ({ ...current, current_company: event.target.value }))}
                    />
                  </label>
                  <label>
                    Resume URL
                    <input
                      value={applyForm.resume_url}
                      onChange={(event) => setApplyForm((current) => ({ ...current, resume_url: event.target.value }))}
                    />
                  </label>
                </div>

                <label>
                  Notes
                  <textarea
                    rows={4}
                    value={applyForm.notes_summary}
                    onChange={(event) => setApplyForm((current) => ({ ...current, notes_summary: event.target.value }))}
                  />
                </label>

                <button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Submitting…' : 'Submit application'}
                </button>
              </form>
            </>
          ) : (
            <p>Choose one of the live openings to review the role and submit an application.</p>
          )}
        </section>
      </section>
    </main>
  );
}